import React, { useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  /** Optional subtitle below the title. */
  description?: string
  children: React.ReactNode
  /** Optional footer (action buttons). */
  footer?: React.ReactNode
  /** Panel max width. Default `sm` (max-w-sm). */
  size?: 'sm' | 'md'
}

/**
 * Lightweight confirmation / form modal. Solid paper surface, no transparency.
 * Closes on Escape or backdrop click.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'sm',
}) => {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const panelMax = size === 'md' ? 'max-w-md' : 'max-w-sm'

  function handleBackdropMouseDown(e: React.MouseEvent) {
    if (!panelRef.current?.contains(e.target as Node)) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="absolute inset-0 bg-ink/40"
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative z-10 w-full ${panelMax} bg-paper border border-rule shadow-lg rounded-sm`}
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2 border-b border-rule">
          <div className="min-w-0 pr-6">
            <h2 id={titleId} className="font-serif text-lg text-ink">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-xs text-ink-500 leading-snug">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Luk"
            className="shrink-0 p-1 text-ink-500 hover:text-ink rounded-sm"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-ink">{children}</div>
        {footer && (
          <div className="px-4 py-3 border-t border-rule flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

export default Modal
